'use client';

import { useCallback, useEffect, useRef } from 'react';

import { Box, BoxProps, Typography } from '@mui/material';
import cx from 'clsx';
import Image from 'next/image';

import ListItem from '@/components/ListItem';
import { Link } from '@/i18n/navigation';
import colors from '@/styles/themes/colors';
import { Image as ImageType } from '@/types/image.type';
import useBreakpoint from '@/utils/hooks/useBreakpoint';
import { useGlowEffect } from '@/utils/hooks/useGlowEffect';
import useIntersectionObserver from '@/utils/hooks/useIntersectionObserver';

import styles from './FeatureCard.module.scss';

const VARIANT_COLORS = {
  primary: colors.blue500,
  secondary: colors.green500,
};

export interface FeatureCardProps extends Omit<BoxProps, 'title'> {
  title: string;
  image: ImageType;
  variant?: 'primary' | 'secondary';
  description?: string;
  listItems?: string[];
  linkText?: string;
  linkHref?: string;
  glowColor?: string;
  poster?: string;
}

const FeatureCard = ({
  title,
  description,
  listItems,
  linkText,
  linkHref,
  image,
  variant = 'primary',
  glowColor,
  poster,
  className,
  ...rest
}: FeatureCardProps) => {
  const { ref, onMouseMove, onMouseLeave, style } = useGlowEffect({
    color: glowColor || VARIANT_COLORS[variant],
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isVideo = image.src.endsWith('.mp4');
  const { isDesktop } = useBreakpoint();

  const isInViewport = useIntersectionObserver(containerRef, {
    threshold: 0.2,
    triggerOnce: false,
    rootMargin: '100px 0px',
  });

  useEffect(() => {
    if (!isVideo || !videoRef.current) return undefined;

    const video = videoRef.current;
    let canPlayHandler: (() => void) | null = null;

    if (!isDesktop) {
      if (isInViewport) {
        const playPromise = video.play();

        if (playPromise !== undefined) {
          playPromise.catch(() => {
            canPlayHandler = () => {
              video.play().catch(() => {});
            };

            video.addEventListener('canplay', canPlayHandler, { once: true });
          });
        }
      } else {
        video.pause();
      }
    }

    return () => {
      if (canPlayHandler) {
        video.removeEventListener('canplay', canPlayHandler);
      }
    };
  }, [isInViewport, isDesktop, isVideo]);

  const handleMouseEnter = useCallback(() => {
    if (isDesktop && isVideo && videoRef.current) {
      videoRef.current.play().catch(() => {
        // Ignore play errors (e.g., if video is not ready)
      });
    }
  }, [isDesktop, isVideo]);

  const handleMouseLeaveCard = useCallback(() => {
    if (onMouseLeave) {
      onMouseLeave();
    }

    if (isDesktop && isVideo && videoRef.current) {
      videoRef.current.pause();
    }
  }, [isDesktop, isVideo, onMouseLeave]);

  return (
    <Link href={linkHref as '/'}>
      <Box
        ref={ref}
        onMouseMove={onMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeaveCard}
        className={cx(styles.container, styles[variant], className)}
        style={style}
        {...rest}
      >
        <Box ref={containerRef} className={styles.imageWrapper}>
          {isVideo ? (
            <video
              ref={videoRef}
              className={styles.image}
              muted
              loop
              playsInline
              preload="metadata"
              poster={poster}
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '16px' }}
            >
              <source src={image.src} type="video/mp4" />
            </video>
          ) : (
            <Image
              src={image.src}
              alt={image.alt}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              style={{ objectFit: 'cover' }}
              className={styles.image}
            />
          )}
        </Box>

        <Box className={styles.content}>
          <Typography
            component="h3"
            variant="h5"
            className={styles.title}
            sx={{
              typography: { xs: 'h5', md: 'h3', fontWeight: '500 !important' },
            }}
          >
            {title}
          </Typography>

          {description && (
            <Typography variant="body1" className={styles.description}>
              {description}
            </Typography>
          )}

          {listItems && listItems.length > 0 && (
            <Box className={styles.listWrapper}>
              {listItems.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <ListItem key={index} text={item} variant={variant === 'primary' ? 'blue' : 'green'} />
              ))}
            </Box>
          )}

          {linkText && linkHref && (
            <Typography variant="body1" className={styles.link}>
              {linkText}
            </Typography>
          )}
          <Box className={cx(styles.ellipse, styles[variant])} />
        </Box>
      </Box>
    </Link>
  );
};

export default FeatureCard;
